import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableRow from "@material-ui/core/TableRow";
import { ClusterList_clusters_edges_node } from "@saleor/cluster/types/ClusterList";
import Checkbox from "@saleor/components/Checkbox";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TableHead from "@saleor/components/TableHead";
import TablePagination from "@saleor/components/TablePagination";
import { maybe, renderCollection } from "@saleor/misc";
import { ListActions, ListProps } from "@saleor/types";
import React from "react";
import { FormattedMessage } from "react-intl";

const useStyles = makeStyles(
  {
    colArea: {
      width: 175
    },
    colContact: {
      width: 200
    },
    colEmail: {
      width: "30%"
    },
    colName: {
      width: "70%"
    },
    colStatus: {
      width: 110
    },
    tableRow: {
      "& td": {
        wordBreak: "break-all"
      },
      cursor: "pointer"
    }
  },
  { name: "MallList" }
);

interface ClusterListProps extends ListProps, ListActions {
  clusters?: ClusterList_clusters_edges_node[];
  onAdd?();
}

const numberOfColumns = 2;

const ClusterList: React.FC<ClusterListProps> = props => {
  const {
    clusters,
    disabled,
    settings,
    pageInfo,
    onNextPage,
    onPreviousPage,
    onUpdateListSettings,
    onRowClick,
    isChecked,
    selected,
    toggle,
    toggleAll,
    toolbar
  } = props;

  const classes = useStyles(props);

  return (
    <ResponsiveTable>
      <TableHead
        colSpan={numberOfColumns + 1}
        disabled={disabled}
        items={clusters}
        selected={selected}
        toggleAll={toggleAll}
        toolbar={toolbar}
      >
        <TableCellHeader className={classes.colName}>
          <FormattedMessage defaultMessage="Name" />
        </TableCellHeader>
        <TableCellHeader className={classes.colEmail}>
          <FormattedMessage
            defaultMessage="Product Type"
            description="number of subcategories"
          />
        </TableCellHeader>
      </TableHead>
      <TableFooter>
        <TableRow>
          <TablePagination
            colSpan={numberOfColumns + 1}
            settings={settings}
            hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
            onNextPage={onNextPage}
            onUpdateListSettings={onUpdateListSettings}
            hasPreviousPage={
              pageInfo && !disabled ? pageInfo.hasPreviousPage : false
            }
            onPreviousPage={onPreviousPage}
          />
        </TableRow>
      </TableFooter>
      <TableBody>
        {renderCollection(
          clusters,
          (mall, index) => {
            const isSelected = mall ? isChecked(mall.id) : false;

            return (
              <TableRow
                className={classes.tableRow}
                hover={!!mall}
                key={mall ? mall.id : "skeleton"}
                data-test="id"
                data-test-id={maybe(() => mall.id)}
                onClick={mall ? onRowClick(mall.id) : undefined}
              >
                <TableCell padding="checkbox" id={`checkboxRow${index}`}>
                  <Checkbox
                    checked={isSelected}
                    disabled={disabled}
                    disableClickPropagation
                    onChange={() => toggle(mall.id)}
                  />
                </TableCell>
                <TableCell
                  className={classes.colName}
                  data-test="name"
                  id={`nameRow${index}`}
                >
                  {mall && mall.name ? mall.name : <Skeleton />}
                </TableCell>
                <TableCell
                  className={classes.colEmail}
                  data-test="email"
                  id={`productTypeRow${index}`}
                >
                  {mall && mall.productType ? (
                    mall.productType.name
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
              </TableRow>
            );
          },
          () => (
            <TableRow>
              <TableCell colSpan={numberOfColumns + 1}>
                <FormattedMessage defaultMessage="No clusters found" />
              </TableCell>
            </TableRow>
          )
        )}
      </TableBody>
    </ResponsiveTable>
  );
};

ClusterList.displayName = "ClusterList";
export default ClusterList;
